/** Cohesive stroke icon set (24×24 viewBox, ~2–3px padding, optical balance). */

import type { Handle } from "remix/ui";

interface IconProps {
  size?: number;
}

function baseProps(size: number) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
    focusable: false,
  };
}

export function IconBack(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

/** Forward chevron — IconBack mirrored, for "the next one". */
export function IconForward(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export function IconFlip(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M16.5 3L19 5.5 16.5 8" />
      <path d="M4.5 11V9a3.5 3.5 0 013.5-3.5H19" />
      <path d="M7.5 21L5 18.5 7.5 16" />
      <path d="M19.5 13v2a3.5 3.5 0 01-3.5 3.5H5" />
    </svg>
  );
}

export function IconTorch(handle: Handle<IconProps & { on?: boolean }>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path
        d="M13 3L7 13h5l-1 8 6-10h-5l1-8z"
        fill={handle.props.on ? "currentColor" : "none"}
      />
    </svg>
  );
}

export function IconTimer(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <circle cx="12" cy="13" r="7" />
      <path d="M12 10v3.5l2 1.2" />
      <path d="M9 3.5h6" />
      <path d="M12 3.5V5" />
    </svg>
  );
}

/** Map-pin outline for location tagging. */
export function IconLocation(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M12 20.5s6.5-4.4 6.5-10a6.5 6.5 0 10-13 0c0 5.6 6.5 10 6.5 10z" />
      <circle cx="12" cy="10.5" r="2.25" />
    </svg>
  );
}

export function IconPlay(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M8 6.5v11L18 12 8 6.5z" />
    </svg>
  );
}

export function IconPause(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M9 6.5v11" />
      <path d="M15 6.5v11" />
    </svg>
  );
}

/** Filmstrip: mirrors the clip timeline the editor button opens. */
export function IconEditor(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <path d="M7.5 4.5v15" />
      <path d="M16.5 4.5v15" />
      <path d="M3 9.5h4.5" />
      <path d="M3 14.5h4.5" />
      <path d="M16.5 9.5H21" />
      <path d="M16.5 14.5H21" />
      <path d="M7.5 12h9" />
    </svg>
  );
}

/** A cut through a track: a blade on the line it divides. */
export function IconSplit(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M12 3v18" />
      <path d="M4 8h5" />
      <path d="M15 8h5" />
      <path d="M4 16h5" />
      <path d="M15 16h5" />
    </svg>
  );
}

/** A strip of frames — a video file, as distinct from the editor it opens in. */
export function IconFilm(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 9.5h18" />
      <path d="M3 14.5h18" />
      <path d="M8 5v14" />
      <path d="M16 5v14" />
    </svg>
  );
}

/** Backspace-style control for deleting the last recorded clip. */
export function IconDeleteLast(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M18.5 6H9.2L4.5 12l4.7 6H18.5a2 2 0 002-2V8a2 2 0 00-2-2z" />
      <path d="M11.5 10l5 5" />
      <path d="M16.5 10l-5 5" />
    </svg>
  );
}

/** Straight-sided bin with a rounded base; sturdier than a tapered can at small sizes. */
export function IconTrash(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M4.5 7h15" />
      <path d="M9.5 7V5.5A1.5 1.5 0 0111 4h2a1.5 1.5 0 011.5 1.5V7" />
      <path d="M18 7v11a2.5 2.5 0 01-2.5 2.5h-7A2.5 2.5 0 016 18V7" />
      <path d="M10 11v5.5" />
      <path d="M14 11v5.5" />
    </svg>
  );
}

export function IconDuplicate(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5V6.5A2 2 0 0013.5 4.5h-7a2 2 0 00-2 2v7a2 2 0 002 2h2" />
    </svg>
  );
}

/** Bracket / I-beam handles that read as “trim”. */
export function IconTrim(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M5 6v12" />
      <path d="M3.5 6h3" />
      <path d="M3.5 18h3" />
      <path d="M19 6v12" />
      <path d="M17.5 6h3" />
      <path d="M17.5 18h3" />
      <path d="M9 12h6" />
    </svg>
  );
}

export function IconChevronDown(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M6 9.5 12 15.5 18 9.5" />
    </svg>
  );
}

export function IconChevronUp(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M6 14.5l6-6 6 6" />
    </svg>
  );
}

export function IconChevronLeft(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M14.5 6l-6 6 6 6" />
    </svg>
  );
}

export function IconChevronRight(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M9.5 6l6 6-6 6" />
    </svg>
  );
}

export function IconPlus(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M12 6v12" />
      <path d="M6 12h12" />
    </svg>
  );
}

export function IconMore(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <circle cx="6" cy="12" r="1.75" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.75" fill="currentColor" stroke="none" />
      <circle cx="18" cy="12" r="1.75" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconUndo(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M7 5L4 8l3 3" />
      <path d="M4 8h7a5 5 0 110 10H9" />
    </svg>
  );
}

/** The iOS share glyph: box with an arrow rising out of it. */
export function IconShareIos(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M8 8H6a1 1 0 00-1 1v11a1 1 0 001 1h12a1 1 0 001-1V9a1 1 0 00-1-1h-2" />
      <path d="M12 14V3" />
      <path d="M8.5 6.5L12 3l3.5 3.5" />
    </svg>
  );
}

export function IconMusic(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

export function IconClose(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </svg>
  );
}

export function IconLens(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.5" />
    </svg>
  );
}

export function IconLock(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 018 0v3" />
    </svg>
  );
}

/** Down arrow into a tray: install / download to this device. */
export function IconDownload(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M12 3.5V14" />
      <path d="M7.5 9.5L12 14l4.5-4.5" />
      <path d="M4.5 16.5v2a2 2 0 002 2h11a2 2 0 002-2v-2" />
    </svg>
  );
}

/** Lowercase "i" in a circle: about / more information. */
export function IconInfo(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11.25v5" />
      <circle cx="12" cy="7.75" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Monitor with a record dot: screen recording. */
export function IconScreen(handle: Handle<IconProps & { on?: boolean }>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="3" y="4.5" width="18" height="12.5" rx="2" />
      <path d="M9 20.5h6" />
      <circle
        cx="12"
        cy="10.75"
        r="2.4"
        fill={handle.props.on ? "currentColor" : "none"}
      />
    </svg>
  );
}

export function IconSettings(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 15a1.7 1.7 0 00.34 1.87l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.7 1.7 0 00-1.87-.34 1.7 1.7 0 00-1.03 1.56V21a2 2 0 11-4 0v-.11a1.7 1.7 0 00-1.11-1.56 1.7 1.7 0 00-1.87.34l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.7 1.7 0 004.6 15a1.7 1.7 0 00-1.56-1.03H3a2 2 0 110-4h.11A1.7 1.7 0 004.6 8.94 1.7 1.7 0 004.34 7l-.06-.06a2 2 0 112.83-2.83l.06.06a1.7 1.7 0 001.87.34H9a1.7 1.7 0 001.03-1.56V3a2 2 0 114 0v.11a1.7 1.7 0 001.03 1.56 1.7 1.7 0 001.87-.34l.06-.06a2 2 0 112.83 2.83l-.06.06a1.7 1.7 0 00-.34 1.87V9a1.7 1.7 0 001.56 1.03H21a2 2 0 110 4h-.11A1.7 1.7 0 0019.4 15z" />
    </svg>
  );
}

/** Wide/desktop layout: a landscape frame. */
export function IconWide(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M9 19v0M15 19v0" />
    </svg>
  );
}

/** Phone layout: a portrait frame. */
export function IconPhone(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M10.5 5.5h3" />
    </svg>
  );
}

/** Microphone, struck through when muted. */
export function IconMic(handle: Handle<IconProps & { muted?: boolean }>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5v4M9 21.5h6" />
      {handle.props.muted ? <path d="M4 3.5l16 17" /> : null}
    </svg>
  );
}

/** Camera, struck through when it is kept out of the recording. */
export function IconCamera(handle: Handle<IconProps & { off?: boolean }>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M4 7.5h9.5a2 2 0 012 2v5a2 2 0 01-2 2H4a2 2 0 01-2-2v-5a2 2 0 012-2z" />
      <path d="M15.5 12.5l5-3v7l-5-3z" />
      {handle.props.off ? <path d="M3 3.5l18 17" /> : null}
    </svg>
  );
}

/**
 * A STILLS camera, as distinct from IconCamera's video body-and-lens.
 *
 * The shutter used to wear the video icon, on the one screen whose whole point
 * is that a tap there takes a photograph rather than a clip.
 */
export function IconShutter(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M3 8.5a2 2 0 012-2h2.2l1.3-2h7l1.3 2H19a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <circle cx="12" cy="12.5" r="3.4" />
    </svg>
  );
}

/** Kept but not in the cut: a tray with something resting in it. */
export function IconAside(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M3 13.5h5l1.5 2.5h5l1.5-2.5h5" />
      <path d="M5.5 4.5h13l2.5 9v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4z" />
    </svg>
  );
}

/** Several things in one frame: a two-by-two of cells. */
export function IconCollage(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="3" y="3.5" width="8" height="7" rx="1.5" />
      <rect x="13" y="3.5" width="8" height="7" rx="1.5" />
      <rect x="3" y="13.5" width="8" height="7" rx="1.5" />
      <rect x="13" y="13.5" width="8" height="7" rx="1.5" />
    </svg>
  );
}

/** Record over an existing clip: a film frame with a camera bubble. */
export function IconOverlay(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2" />
      <path d="M6.5 4.5v15" />
      <circle cx="16.5" cy="14.5" r="3.4" fill="currentColor" />
    </svg>
  );
}

/* Screen-recording composition: each icon is a little map of the output frame,
   so the picker reads at a glance without labels. */

/** Screen only: one full frame. */
export function IconLayoutScreen(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 20)}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2" />
    </svg>
  );
}

/** Screen with a camera bubble in the corner. */
export function IconLayoutPip(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 20)}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2" />
      <circle cx="16.75" cy="15" r="3.25" fill="currentColor" />
    </svg>
  );
}

/** Camera cut out and standing over the frame. */
export function IconLayoutFloat(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 20)}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2" />
      {/* A head-and-shoulders silhouette rising off the bottom edge. */}
      <circle cx="15.5" cy="10.5" r="2.2" fill="currentColor" stroke="none" />
      <path
        d="M11.6 19.5a3.9 3.9 0 017.8 0z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

/** Screen and camera side by side. */
export function IconLayoutSide(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 20)}>
      <rect x="2.5" y="4.5" width="11.5" height="15" rx="2" />
      <rect x="16.5" y="4.5" width="5" height="15" rx="2" fill="currentColor" />
    </svg>
  );
}

/** Notes: a page with written lines. Opens the note editor. */
export function IconNotes(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M6 3.5h9l4 4v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1Z" />
      <path d="M15 3.5v4h4" />
      <path d="M8.5 12h7" />
      <path d="M8.5 15.5h7" />
      <path d="M8.5 8.5h3" />
    </svg>
  );
}

/** A card: a frame with a line of type across the middle. */
export function IconCard(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <path d="M7.5 11h9" />
      <path d="M7.5 14.5h5.5" />
    </svg>
  );
}

/**
 * Move to another section: an arrow crossing into a second container.
 *
 * Not a plain arrow — that reads as "next" beside the reorder controls it sits
 * with, which is the one meaning it must not have here.
 */
export function IconMoveTo(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M10.5 5.5H5.5a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h5" />
      <path d="M19.5 12H11" />
      <path d="m15.5 8 4 4-4 4" />
    </svg>
  );
}

/**
 * Sections and elements: stacked plates.
 *
 * Stacked rather than a list, because a list is what the timeline already looks
 * like — the thing this opens is the project in LAYERS, sections holding
 * elements, and the icon has to say which of the two screens it goes to.
 */
export function IconLayers(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M12 3.5 20.5 8 12 12.5 3.5 8 12 3.5Z" />
      <path d="M3.5 12.5 12 17l8.5-4.5" />
      <path d="M3.5 17 12 21.5l8.5-4.5" />
    </svg>
  );
}

/** Teleprompter: lines of script with a scroll arrow. Reads over the camera. */
/** Burned-in captions: a frame with a line of text across its foot. */
export function IconSubtitles(handle: Handle<IconProps & { on?: boolean }>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M6.5 15h6" />
      <path d="M15 15h2.5" />
      {handle.props.on ? <path d="M6.5 11.5h11" /> : null}
    </svg>
  );
}

export function IconPrompter(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M6.5 7.5h11" />
      <path d="M6.5 10.5h11" />
      <path d="M6.5 13h7" />
      <path d="M12 18v3" />
      <path d="M9.5 19.5 12 22l2.5-2.5" />
    </svg>
  );
}

/**
 * Dual camera: a frame with a second frame inset in it — the composite the
 * mode produces, rather than two camera glyphs side by side (which read as a
 * lens picker at 22px).
 */
export function IconDualCamera(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <rect x="12" y="12" width="8" height="6.5" rx="1.5" />
      <circle cx="8" cy="10" r="2.6" />
    </svg>
  );
}

/** Refresh: a circular arrow. Reloads the app onto the current build. */
export function IconRefresh(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M20 12a8 8 0 1 1-2.4-5.7" />
      <path d="M20 4v4.5h-4.5" />
    </svg>
  );
}

/** Sparkle: the AI assistant. A four-point star with a smaller companion. */
export function IconSparkle(handle: Handle<IconProps>) {
  return () => (
    <svg {...baseProps(handle.props.size ?? 22)}>
      <path d="M11 3.5 12.6 8 17 9.6l-4.4 1.6L11 15.7 9.4 11.2 5 9.6 9.4 8 11 3.5Z" />
      <path d="M17.5 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" />
    </svg>
  );
}
